import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Ordered compaction stages for the desktop storefront header. The flex
 * layout already shrinks the search field and then the delivery-method
 * selector down to their min widths. Once those are exhausted the row would
 * overflow, so we step the remaining fixed-width actions down to their
 * icon-only variants in a deterministic order: cart first, then profile.
 */
const HEADER_COMPACTION_STAGE = {
	none: 0,
	cartIconOnly: 1,
	profileIconOnly: 2,
} as const;

const MAX_COMPACTION_STAGE = HEADER_COMPACTION_STAGE.profileIconOnly;
/** Extra width required before a collapsed action is restored, avoiding flip-flop on resize. */
const EXPAND_HYSTERESIS_PX = 24;
/** Sub-pixel rounding guard when comparing scroll vs client width. */
const OVERFLOW_TOLERANCE_PX = 1;

export interface HeaderCompactionState {
	isCartIconOnly: boolean;
	isProfileIconOnly: boolean;
}

interface ResolveCompactionStageParams {
	currentStage: number;
	isOverflowing: boolean;
	clientWidth: number;
	collapseWidths: ReadonlyArray<number | undefined>;
}

/**
 * Pure decision for the next compaction stage. Steps up when the header
 * overflows and steps back down only once the viewport is wider than the
 * width at which the previous stage collapsed (plus a hysteresis buffer).
 */
export function resolveCompactionStage({
	currentStage,
	isOverflowing,
	clientWidth,
	collapseWidths,
}: ResolveCompactionStageParams): number {
	if (isOverflowing) {
		return Math.min(currentStage + 1, MAX_COMPACTION_STAGE);
	}
	if (currentStage === HEADER_COMPACTION_STAGE.none) {
		return currentStage;
	}
	const widthWhenCollapsed = collapseWidths[currentStage - 1];
	const canExpand =
		widthWhenCollapsed === undefined ||
		clientWidth > widthWhenCollapsed + EXPAND_HYSTERESIS_PX;
	return canExpand ? currentStage - 1 : currentStage;
}

/**
 * Tracks how aggressively the desktop header must compact its trailing
 * actions based on available width, returning the cart/profile icon-only
 * flags in priority order.
 */
export function useHeaderCompaction<T extends HTMLElement>(): {
	containerRef: React.RefObject<T | null>;
	compaction: HeaderCompactionState;
} {
	const containerRef = useRef<T>(null);
	const [stage, setStage] = useState<number>(HEADER_COMPACTION_STAGE.none);
	const stageRef = useRef<number>(HEADER_COMPACTION_STAGE.none);
	const collapseWidthsRef = useRef<Array<number | undefined>>([]);

	const evaluate = useCallback(() => {
		const element = containerRef.current;
		if (!element) {
			return;
		}
		const isOverflowing =
			element.scrollWidth > element.clientWidth + OVERFLOW_TOLERANCE_PX;
		const currentStage = stageRef.current;
		const nextStage = resolveCompactionStage({
			currentStage,
			isOverflowing,
			clientWidth: element.clientWidth,
			collapseWidths: collapseWidthsRef.current,
		});
		if (nextStage === currentStage) {
			return;
		}
		if (nextStage > currentStage) {
			collapseWidthsRef.current[currentStage] = element.clientWidth;
		}
		stageRef.current = nextStage;
		setStage(nextStage);
	}, []);

	useEffect(() => {
		const element = containerRef.current;
		if (!element || typeof ResizeObserver === "undefined") {
			return;
		}
		const observer = new ResizeObserver(() => evaluate());
		observer.observe(element);
		evaluate();
		return () => observer.disconnect();
	}, [evaluate]);

	useEffect(() => {
		if (stage < MAX_COMPACTION_STAGE) {
			evaluate();
		}
	}, [stage, evaluate]);

	return {
		containerRef,
		compaction: {
			isCartIconOnly: stage >= HEADER_COMPACTION_STAGE.cartIconOnly,
			isProfileIconOnly: stage >= HEADER_COMPACTION_STAGE.profileIconOnly,
		},
	};
}
